// masterentity.service.ts  (full replacement)
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../../core/_base/crud';
import { MasterEntity } from '../../../core/auth/_models/masterentity.model';

const API_ENTITY_URL = '/api/entity';
const API_PAGINATED = '/api/entityw/get';
const API_PRICE = '/api/entity/price';
const API_PERMISSIONS = '/api/entity/price/permissions';

export interface PaginatedResponse {
  data: any[];
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}
export interface CurrencyConversionDialogData {
  sourceCurrency: 'USD' | 'AUD';
  targetCurrency: 'USD' | 'AUD';
  exchangeRate: number;
}

export interface BulkPayload {
  entities: MasterEntity[];
  logedUserCompany: string;
  userId: string;
  adminId: string;
  role: string;
  priceChangeMode: 'main' | 'own';
  sourceCurrency: string;  
  targetCurrency: string;  
}

export interface PriceUpdatePayload {
  orderCode: string;
  price: number;
  userId: string;
  role: string;
  adminId: string;
  priceChangeMode: 'main' | 'own';
  logedUserCompany: string;
  sourceCurrency: string;  
  targetCurrency: string;  
}

export interface PermissionEntry {
  userId: string;
  fullname: string;
  email: string;
  grantedBy: string;
  grantedAt: string;
}

@Injectable()
export class MasterEntityService {
  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService
  ) { }

  // ─── Create ────────────────────────────────────────────────────────────────

  createEntity(entity: MasterEntity): Observable<MasterEntity> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<MasterEntity>(API_ENTITY_URL, entity, {
      headers: httpHeaders,
      responseType: 'text' as 'json',
    });
  }

  createEntityinBulk(payload: BulkPayload): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<any>(`${API_ENTITY_URL}/inBulk`, payload, {
      headers: httpHeaders,
    });
  }

  // ─── Read ──────────────────────────────────────────────────────────────────

  getPaginated(
    page: number,
    limit: number,
    logedUserCompany: string,
    search: string = '',
    userId: string = '',
    filterUserId: string = '',
    priceChangeMode: string = 'own'   // ← add
  ): Observable<PaginatedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('logedUserCompany', logedUserCompany);

    if (search) { params = params.set('search', search); }
    if (userId) { params = params.set('userId', userId); }
    if (filterUserId) { params = params.set('filterUserId', filterUserId); }
    if (priceChangeMode) { params = params.set('priceChangeMode', priceChangeMode); }  // ← add

    return this.http.get<PaginatedResponse>(API_PAGINATED, { params });
  }

  getAll(): Observable<MasterEntity[]> {
    return this.http.get<MasterEntity[]>(API_ENTITY_URL);
  }

  getEntityById(entityId: string): Observable<MasterEntity> {
    return this.http.get<MasterEntity>(`${API_ENTITY_URL}/${entityId}`);
  }

  getAllByFilter(entity: any): Observable<MasterEntity> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<MasterEntity>(`${API_ENTITY_URL}/filter`, entity, {
      headers: httpHeaders,
      responseType: 'text' as 'json',
    });
  }

  findEntity(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    const httpParams = this.httpUtils.getFindHTTPParams(queryParams);
    return this.http.get<QueryResultsModel>(`${API_ENTITY_URL}/find`, {
      headers: httpHeaders,
      params: httpParams,
      responseType: 'text' as 'json',
    });
  }

  // ─── Update ────────────────────────────────────────────────────────────────

  updateEntity(entity: MasterEntity): Observable<any> {
    const httpHeader = this.httpUtils.getHTTPHeaders();
    return this.http.put(
      `${API_ENTITY_URL}/${entity.uuid}`,
      JSON.stringify(entity),
      { headers: httpHeader, responseType: 'text' as 'json' }
    );
  }

  updateStatusForEntity(entities: MasterEntity[], status: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.put(
      `${API_ENTITY_URL}/updateStatus`,
      { customersForUpdate: entities, newStatus: status },
      { headers: httpHeaders, responseType: 'text' as 'json' }
    );
  }

  updateEntityPrice(payload: PriceUpdatePayload): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<any>(`${API_PRICE}/update`, payload, { headers: httpHeaders });
  }

  getUserPricesList(
    page: number,
    limit: number,
    filterUserId: string = '',
    search: string = ''
  ): Observable<PaginatedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (filterUserId) { params = params.set('filterUserId', filterUserId); }
    if (search) { params = params.set('search', search); }

    return this.http.get<PaginatedResponse>(`${API_PRICE}/user-list`, { params });
  }

  // ─── Permission Management ─────────────────────────────────────────────────

  /** Returns all users who currently have price-update permission */
  getPermittedUsers(): Observable<PermissionEntry[]> {
    return this.http.get<PermissionEntry[]>(API_PERMISSIONS);
  }

  /** Grants price-update permission to a user */
  grantPermission(userId: string, fullname: string, email: string, grantedBy: string): Observable<any> {
    return this.http.post<any>(API_PERMISSIONS, { userId, fullname, email, grantedBy });
  }

  /** Revokes price-update permission from a user */
  revokePermission(userId: string): Observable<any> {
    return this.http.delete<any>(`${API_PERMISSIONS}/${userId}`);
  }

  /** Quick permission check for a single user */
  checkPermission(userId: string): Observable<{ userId: string; permitted: boolean }> {
    return this.http.get<{ userId: string; permitted: boolean }>(
      `${API_PERMISSIONS}/check/${userId}`
    );
  }

  // ─── Delete ────────────────────────────────────────────────────────────────

  deleteEntity(entityId: string): Observable<MasterEntity> {
    return this.http.delete<MasterEntity>(`${API_ENTITY_URL}/${entityId}`);
  }

  deleteEntities(ids: string[] = []): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.put<QueryResultsModel>(
      `${API_ENTITY_URL}/deleteProjects`,
      { projectIdsForDelete: ids },
      { headers: httpHeaders, responseType: 'text' as 'json' }
    );
  }
// masterentity.service.ts - Update the exportAll method

exportAll(
  logedUserCompany: string,
  search: string = '',
  userId: string = '',
  filterUserId: string = '',
  priceChangeMode: string = 'own',
  exportCurrency: string = 'USD'  // Add this parameter
): Observable<{ data: any[]; totalRecords: number; exportCurrency: string }> {
  let params = new HttpParams()
    .set('logedUserCompany', logedUserCompany)
    .set('exportCurrency', exportCurrency);  // Add to params

  if (search) { params = params.set('search', search); }
  if (userId) { params = params.set('userId', userId); }
  if (filterUserId) { params = params.set('filterUserId', filterUserId); }
  if (priceChangeMode) { params = params.set('priceChangeMode', priceChangeMode); }

  return this.http.get<{ data: any[]; totalRecords: number; exportCurrency: string }>(
    '/api/entityw/export', { params }
  );
}
  getExchangeRate(): Observable<{ rate: number; timestamp: string }> {
  return this.http.get<{ rate: number; timestamp: string }>(
    `${API_PRICE}/../currency/rate`
  );
}

getCurrencyPreference(userId: string): Observable<{ preferredCurrency: string }> {
  return this.http.get<{ preferredCurrency: string }>(
    `${API_PRICE}/../currency/preference/${userId}`
  );
}

updateCurrencyPreference(userId: string, preferredCurrency: string): Observable<any> {
  return this.http.put<any>(
    `${API_PRICE}/../currency/preference`,
    { userId, preferredCurrency }
  );
}
}